// src/components/CardDisplay.tsx - Enhanced with improved styling compatibility
import React from 'react';
import { Card } from '../types';

interface CardDisplayProps {
  card: Card;
  showDetails?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  selected?: boolean;
  compact?: boolean; // New prop for compact display mode
}

const CardDisplay: React.FC<CardDisplayProps> = ({ 
  card, 
  showDetails = false, 
  onClick, 
  disabled = false,
  selected = false,
  compact = false
}) => {
  const getCardTypeColor = (type: string) => {
    switch (type) {
      case 'defensive': return 'border-blue-500 bg-blue-900/20';
      case 'offensive': return 'border-red-500 bg-red-900/20';
      case 'utility': return 'border-green-500 bg-green-900/20';
      default: return 'border-gray-500 bg-gray-900/20';
    }
  };

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common': return 'text-gray-400';
      case 'rare': return 'text-blue-400';
      case 'legendary': return 'text-yellow-400';
      default: return 'text-gray-400';
    }
  };

  const getRarityGlow = (rarity: string) => {
    switch (rarity) {
      case 'legendary': return 'shadow-lg shadow-yellow-500/20';
      case 'rare': return 'shadow-lg shadow-blue-500/20';
      default: return '';
    }
  };

  // Compact mode for last played card display
  if (compact) {
    return (
      <div className={`
        relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg border-2 transition-all duration-300
        ${getCardTypeColor(card.card_type)}
        ${getRarityGlow(card.rarity)}
        ${disabled ? 'opacity-60' : 'hover:scale-105 hover:shadow-xl'}
        w-full max-w-xs
      `}>
        {/* Compact Header */}
        <div className="p-2 border-b border-gray-600">
          <div className="flex justify-between items-start">
            <h3 className="font-bold text-white text-xs leading-tight">{card.name}</h3>
            <span className={`text-xs font-medium ${getRarityColor(card.rarity)}`}>
              {card.rarity.charAt(0).toUpperCase()}
            </span>
          </div>
        </div>

        {/* Compact Image */}
        <div className="relative aspect-square bg-gray-700">
          <img 
            src={card.image_url} 
            alt={card.name}
            className="w-full h-full object-cover"
            loading="lazy"
            decoding="async"
            onError={(e) => {
              e.currentTarget.src = '/cards/placeholder.png';
            }}
          />
          
          {/* Stats overlay */}
          <div className="absolute bottom-1 left-1 right-1 flex justify-between">
            {card.attack_value > 0 && (
              <div className="bg-red-600 text-white text-xs font-bold px-1.5 py-0.5 rounded">
                {card.attack_value}
              </div>
            )}
            {card.defense_value > 0 && (
              <div className="bg-blue-600 text-white text-xs font-bold px-1.5 py-0.5 rounded">
                {card.defense_value}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Standard card display
  return (
    <div 
      className={`
        relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg border-2 transition-all duration-300 cursor-pointer
        ${getCardTypeColor(card.card_type)}
        ${getRarityGlow(card.rarity)}
        ${selected ? 'ring-2 ring-yellow-400 scale-105 shadow-xl shadow-yellow-400/30' : ''}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 hover:shadow-xl'}
        ${onClick ? 'transform' : ''}
      `}
      onClick={disabled ? undefined : onClick}
    >
      {/* Card Header */}
      <div className="p-3 border-b border-gray-600">
        <div className="flex justify-between items-start">
          <h3 className="font-bold text-white text-sm leading-tight">{card.name}</h3>
          <span className={`text-xs font-medium ${getRarityColor(card.rarity)}`}>
            {card.rarity.toUpperCase()}
          </span>
        </div>
        <span className="text-xs text-gray-400 uppercase tracking-wide">
          {card.card_type}
        </span>
      </div>

      {/* Card Image - Enhanced with better styling */}
      <div className="relative aspect-square bg-gradient-to-br from-gray-700 to-gray-800">
        <img 
          src={card.image_url} 
          alt={card.name}
          className="w-full h-full object-cover transition-all duration-300"
          loading="lazy"
          decoding="async"
          onError={(e) => {
            // Fallback to placeholder if image fails to load
            e.currentTarget.src = '/cards/placeholder.png';
          }}
        />
        
        {/* Enhanced Attack/Defense overlay */}
        <div className="absolute bottom-2 left-2 right-2 flex justify-between">
          {card.attack_value > 0 && (
            <div className="bg-gradient-to-r from-red-600 to-red-500 text-white text-xs font-bold px-2 py-1 rounded shadow-lg border border-red-400">
              ATK: {card.attack_value}
            </div>
          )}
          {card.defense_value > 0 && (
            <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white text-xs font-bold px-2 py-1 rounded shadow-lg border border-blue-400">
              DEF: {card.defense_value}
            </div>
          )}
        </div>

        {/* Rarity glow effect */}
        {card.rarity === 'legendary' && (
          <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/10 to-transparent pointer-events-none rounded" />
        )}
        {card.rarity === 'rare' && (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-400/10 to-transparent pointer-events-none rounded" />
        )}
      </div>

      {/* Card Effect */}
      {showDetails && (
        <div className="p-3">
          <p className="text-xs text-gray-300 leading-relaxed">
            {card.description}
          </p>
        </div>
      )}

      {/* Card Stats Summary (when not showing details) */}
      {!showDetails && (
        <div className="p-2 text-center">
          <div className="flex justify-center space-x-4 text-xs">
            <span className="text-red-400 flex items-center">
              ⚔️ {card.attack_value}
            </span>
            <span className="text-blue-400 flex items-center">
              🛡️ {card.defense_value}
            </span>
          </div>
        </div>
      )}

      {/* Selection indicator */}
      {selected && (
        <div className="absolute top-2 right-2 z-10">
          <div className="w-6 h-6 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center shadow-lg border border-yellow-300">
            <span className="text-black text-xs font-bold">✓</span>
          </div>
        </div>
      )}

      {/* Enhanced hover effect */}
      {!disabled && onClick && (
        <div className="absolute inset-0 bg-gradient-to-br from-white/0 to-white/5 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-lg pointer-events-none" />
      )}

      {/* Price tag for shop display */}
      {card.price && (
        <div className="absolute top-2 left-2">
          <div className={`px-2 py-1 rounded text-xs font-bold ${
            card.rarity === 'legendary' ? 'bg-gradient-to-r from-yellow-600 to-yellow-500 text-black' :
            card.rarity === 'rare' ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white' :
            'bg-gradient-to-r from-gray-600 to-gray-500 text-white'
          } shadow-lg`}>
            {card.price} CC
          </div>
        </div>
      )}
    </div>
  );
};

export default CardDisplay;